import React, { useContext, useReducer, createContext } from 'react';

const normalizeFilterName = (filter: string): string => {
  return filter.toLowerCase();
};

interface ProjectsFiltersContextType {
  filters: Map<string, string>;
  dispatch: React.Dispatch<any>;
}

const FiltersContext = createContext<ProjectsFiltersContextType | null>(null);

const reducer = (
  previousState: Map<string, string>,
  action: { type: string; filter: string; value?: string }
) => {
  switch (action.type) {
    case 'UPDATE_FILTER': {
      // Clone state from previous state
      const state = new Map(previousState);

      const { filter, value } = action;
      if (value === undefined) return state;

      const normalizedFilter = normalizeFilterName(filter);

      // Remove any existing filters with the same normalized name
      Array.from(state.keys()).forEach((key) => {
        if (normalizeFilterName(key) === normalizedFilter) {
          state.delete(key);
        }
      });

      // Set the new filter
      state.set(filter, value);

      return state;
    }

    case 'REMOVE_FILTER': {
      // Clone state from previous state
      const state = new Map(previousState);
      const { filter } = action;

      const normalizedFilter = normalizeFilterName(filter);

      let found = false;
      Array.from(state.keys()).forEach((key) => {
        if (normalizeFilterName(key) === normalizedFilter) {
          state.delete(key);
          found = true;
        }
      });

      // If no filter was found, return the previous state
      if (!found) {
        return previousState;
      }

      return state;
    }

    case 'RESET':
      return new Map();

    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
};

const FiltersContextProvider = (props: { children: React.ReactNode }) => {
  const [filters, dispatch] = useReducer(reducer, new Map<string, string>());
  const { children } = props;
  return (
    <FiltersContext.Provider value={{ filters, dispatch }}>
      {children}
    </FiltersContext.Provider>
  );
};

const useProjectsFilters = () => useContext(FiltersContext);

export { FiltersContextProvider };
export default useProjectsFilters;
